import React from "react";
import { Link } from "@/lib/router-compat";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import CrisisLifelineBanner from "@/components/CrisisLifelineBanner";
import SEOHead from "@/components/SEOHead";
import Breadcrumbs from "@/components/Breadcrumbs";
import { RelatedPages, type RelatedLink } from "@/components/RelatedPages";
import { Crumb } from "@/components/schemaConstants";
import { SaintVideoSection } from "@/components/SaintVideoSection";
import { SaintDisclaimerNotice } from "@/components/SaintDisclaimerNotice";
import SaintFiveChecksBox from "@/components/SaintFiveChecksBox";

export interface LearnSection {
  /** Section heading — written as a question where possible. */
  heading: string;
  /** Body content (React nodes) for the section. */
  body: React.ReactNode;
}

export interface LearnFAQ {
  question: string;
  answer: string;
}

export interface LearnSource {
  citation: string;
  url?: string;
}

export interface LearnReviewBlock {
  writtenBy?: string;
  reviewedBy?: string;
  reviewedByHref?: string;
  published?: string;
  lastReviewed?: string;
  lastUpdated?: string;
}

export interface TOCItem {
  label: string;
  href: string;
}

export interface CTABand {
  headline: string;
  primaryText: string;
  primaryHref: string;
  secondaryText: string;
  secondaryHref: string;
  phone: string;
}

export interface LearnPageLayoutProps {
  title: string;
  description: string;
  canonical: string;
  h1: string;
  breadcrumbLabel: string;
  crumbs: Crumb[];
  /** Short-answer box shown at the very top. */
  shortAnswer: string;
  /** Key takeaways bullet list. */
  keyTakeaways: string[];
  sections: LearnSection[];
  faqs: LearnFAQ[];
  sources: LearnSource[];
  /** JSON-LD object (MedicalWebPage + FAQPage + BreadcrumbList @graph). */
  schemaJson: object;
  relatedLinks: RelatedLink[];
  /** Optional authorship / medical-review block. */
  reviewBlock?: LearnReviewBlock;
  /** Optional table of contents (renders after short answer). */
  toc?: TOCItem[];
  /** Optional safety block text (renders after sources). */
  safetyBlock?: string;
  /** Optional trademark footer line (renders after CTA). */
  trademarkFooter?: string;
  /** Optional custom CTA band (overrides default). */
  ctaBand?: CTABand;
}

const PHONE = "954-827-9793";

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

const CrisisNotice: React.FC = () => (
  <aside
    className="not-prose my-8 rounded-xl border border-destructive/30 bg-destructive/5 p-5"
    role="note"
  >
    <p className="text-sm font-medium text-primary">
      If you are thinking about harming yourself, call or text{" "}
      <a
        href="tel:988"
        className="font-bold text-destructive underline underline-offset-2"
      >
        988
      </a>{" "}
      (Suicide &amp; Crisis Lifeline) in the U.S., or go to the nearest
      emergency room.
    </p>
  </aside>
);

const LearnPageLayout: React.FC<LearnPageLayoutProps> = ({
  title,
  description,
  canonical,
  h1,
  crumbs,
  shortAnswer,
  keyTakeaways,
  sections,
  faqs,
  sources,
  schemaJson,
  relatedLinks,
  reviewBlock,
  toc,
  safetyBlock,
  trademarkFooter,
  ctaBand,
}) => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={title}
        description={description}
        canonical={canonical}
        schemaJson={[schemaJson]}
        keywords="TMS therapy, transcranial magnetic stimulation, depression treatment, FDA-cleared TMS, TMS for depression, Neuro Wellness TMS"
      />
      <Navbar />
      <main>
        {/* Hero / header */}
        <section className="bg-gradient-hero py-10 sm:py-14">
          <div className="container-px mx-auto max-w-3xl">
            <Breadcrumbs crumbs={crumbs} className="mb-6" />
            <h1 className="font-headings text-2xl font-extrabold leading-tight text-primary sm:text-3xl md:text-4xl">
              {h1}
            </h1>
            {reviewBlock && (
              <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {reviewBlock.writtenBy && (
                  <span>Written by {reviewBlock.writtenBy}</span>
                )}
                {reviewBlock.reviewedBy && (
                  <span>
                    Medically reviewed by{" "}
                    {reviewBlock.reviewedByHref ? (
                      <a
                        href={reviewBlock.reviewedByHref}
                        className="font-medium text-primary underline underline-offset-2 hover:opacity-80"
                      >
                        {reviewBlock.reviewedBy}
                      </a>
                    ) : (
                      <span className="font-medium text-primary">
                        {reviewBlock.reviewedBy}
                      </span>
                    )}
                  </span>
                )}
                {reviewBlock.published && (
                  <span>Published {reviewBlock.published}</span>
                )}
                {reviewBlock.lastReviewed && (
                  <span>
                    Last medically reviewed {reviewBlock.lastReviewed}
                  </span>
                )}
                {reviewBlock.lastUpdated && (
                  <span>Last updated {reviewBlock.lastUpdated}</span>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Article body */}
        <section className="py-10 sm:py-12">
          <div className="container-px mx-auto max-w-3xl">
            {/* Short answer */}
            <div className="mb-8 rounded-2xl border border-accent/30 bg-accent/5 p-5 sm:p-6">
              <p className="text-base leading-relaxed text-foreground sm:text-[1.0625rem]">
                {shortAnswer}
              </p>
            </div>

            {/* Table of contents */}
            {toc && toc.length > 0 && (
              <nav
                className="mb-10 rounded-2xl border border-border bg-muted/20 p-5 sm:p-6"
                aria-label="Table of contents"
              >
                <h2 className="font-headings text-base font-bold text-primary mb-3">
                  On this page
                </h2>
                <ol className="space-y-1.5 text-sm">
                  {toc.map((item, i) => (
                    <li key={i}>
                      <a
                        href={item.href}
                        className="text-foreground/80 underline underline-offset-2 hover:text-accent"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            )}

            {/* Key takeaways */}
            <div className="mb-10">
              <h2 className="font-headings text-lg font-bold text-primary mb-3">
                Key takeaways
              </h2>
              <ul className="space-y-2">
                {keyTakeaways.map((t, i) => (
                  <li key={i} className="flex gap-3 text-foreground">
                    <span
                      aria-hidden="true"
                      className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent"
                    />
                    <span className="text-[1.0625rem] leading-relaxed">
                      {t}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Prose sections */}
            <div className="space-y-8">
              {sections.map((s, i) => (
                <div
                  key={i}
                  id={s.heading ? slugify(s.heading) : undefined}
                  className="scroll-mt-20"
                >
                  {s.heading && (
                    <h2
                      className="font-headings text-xl font-bold text-primary mb-3 sm:text-2xl"
                      id={slugify(s.heading)}
                    >
                      {s.heading}
                    </h2>
                  )}
                  <div className="space-y-4 text-[1.0625rem] leading-relaxed text-foreground">
                    {s.body}
                  </div>
                </div>
              ))}
            </div>

            {/* Is it really SAINT®? 5 checks — on every /learn article */}
            <div className="mt-10">
              <SaintFiveChecksBox />
            </div>

            {/* Patient Journey video — shown on every /learn article */}
            <div className="mt-10">
              <SaintVideoSection variant="compact" />
            </div>

            {/* FAQ */}
            <div className="mt-12 scroll-mt-20" id="faq">
              <h2 className="font-headings text-xl font-bold text-primary mb-5 sm:text-2xl">
                Frequently Asked Questions
              </h2>
              <div className="space-y-3">
                {faqs.map((f, i) => (
                  <details
                    key={i}
                    className="group rounded-xl border border-border bg-card p-5 shadow-soft [&_p]:text-[1.0625rem] [&_p]:leading-relaxed"
                  >
                    <summary className="flex cursor-pointer items-center justify-between font-headings text-base font-bold text-primary marker:content-['']">
                      {f.question}
                      <span
                        aria-hidden="true"
                        className="ml-3 shrink-0 text-accent transition-transform group-open:rotate-45"
                      >
                        +
                      </span>
                    </summary>
                    <p className="mt-3 text-foreground">{f.answer}</p>
                  </details>
                ))}
              </div>
            </div>

            {/* Crisis notice */}
            <CrisisNotice />

            {/* Sources / Cited Research */}
            {sources.length > 0 && (
              <div className="mt-8 scroll-mt-20" id="cited-research">
                <h2 className="font-headings text-base font-bold text-primary mb-3">
                  Cited Research
                </h2>
                <ol className="space-y-1.5 text-sm text-muted-foreground">
                  {sources.map((s, i) => (
                    <li key={i} className="leading-relaxed">
                      <span className="font-medium text-foreground">
                        {i + 1}.{" "}
                      </span>
                      {s.url ? (
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noopener"
                          className="break-words underline underline-offset-2 hover:text-accent"
                        >
                          {s.citation}
                        </a>
                      ) : (
                        s.citation
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Safety block */}
            {safetyBlock && (
              <div className="mt-8 rounded-xl border border-destructive/30 bg-destructive/5 p-5">
                <h2 className="font-headings text-base font-bold text-primary mb-2">
                  Important Safety Information
                </h2>
                <p className="text-sm leading-relaxed text-foreground/85">
                  {safetyBlock}
                </p>
              </div>
            )}

            {/* SAINT® disclaimer notice — on every /learn article */}
            <SaintDisclaimerNotice />
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-navy py-12 sm:py-14">
          <div className="container-px mx-auto max-w-3xl text-center">
            <h2 className="font-headings text-2xl font-bold text-white sm:text-3xl">
              {ctaBand?.headline ||
                "Have questions about whether TMS may be appropriate for you?"}
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-white/85">
              Speak with our clinical team. A free consultation is the best way
              to get answers specific to your situation.
            </p>
            <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Link
                to={ctaBand?.primaryHref || "/am-i-a-candidate"}
                className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-accent px-6 py-3 font-bold text-accent-foreground shadow-glow transition hover:opacity-90 sm:w-auto"
              >
                {ctaBand?.primaryText || "Request a Free Consultation"}
              </Link>
              <Link
                to={
                  ctaBand?.secondaryHref || "/contents/tms/what-is-tms-therapy"
                }
                className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg border border-white/40 px-6 py-3 font-semibold text-white transition hover:bg-white/10 sm:w-auto"
              >
                {ctaBand?.secondaryText || "Explore TMS Treatment Options"}
              </Link>
              <a
                href={`tel:${(ctaBand?.phone || PHONE).replace(/-/g, "")}`}
                className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg border border-white/40 px-6 py-3 font-semibold text-white transition hover:bg-white/10 sm:w-auto"
              >
                Call {ctaBand?.phone || PHONE}
              </a>
            </div>
          </div>
        </section>

        {/* Trademark footer */}
        {trademarkFooter && (
          <div className="border-t border-white/10 bg-gradient-navy py-4">
            <div className="container-px mx-auto max-w-3xl">
              <p className="text-center text-xs text-white/60">
                {trademarkFooter}
              </p>
            </div>
          </div>
        )}

        <RelatedPages
          heading="Related Patient Education"
          links={relatedLinks}
        />
      </main>
      <div className="container-px mx-auto max-w-3xl px-4 py-6">
        <CrisisLifelineBanner />
      </div>
      <Footer />
    </div>
  );
};

export default LearnPageLayout;
